import { client } from "@/services/apollo.service"
import CAT_ORGANIZATIONS_QUERY from "@/graphql/cat_organizations.gql"
import GET_CAT_ORGANIZATION from "@/graphql/cat_organization.gql"
import GET_CAT_ORGANIZATION_APP_IDS from "@/graphql/cat_organization_app_ids.gql"
import GET_CAT_ORGANIZATION_CLIENT_IDS from "@/graphql/cat_organization_clients.gql"
import GET_CAT_ORGANIZATION_USERS from "@/graphql/cat_organization_users.gql"
import CREATE_CAT_ORGANIZATION_MUTATION from "@/graphql/create_cat_organization_mutation.gql"
import UPDATE_CAT_ORGANIZATION_MUTATION from "@/graphql/update_cat_organization_mutation.gql"
import DELETE_CAT_ORGANIZATION_MUTATION from "@/graphql/delete_cat_organization_mutation.gql"
import CREATE_CAT_ORG_APP from "@/graphql/add_app_id_to_organization_mutation.gql"
import UPDATE_CAT_ORG_APP from "@/graphql/update_cat_organization_application_mutation.gql"
import DELETE_CAT_ORG_APP from "@/graphql/remove_app_id_from_organization_mutation.gql"
import CREATE_CAT_ORG_CLIENT from "@/graphql/add_client_id_to_organization_mutation.gql"
import DELETE_CAT_ORG_CLIENT from "@/graphql/remove_client_id_from_organization_mutation.gql"
import { emitAlert } from "@/helpers/alert.helper"
import { EVENTS } from "@/constants"
import { Commit } from "vuex"

export default {
  async loadOrganizations({ commit }: { commit: Commit }) {
    commit("setLoading", true)
    try {
      const response = await client.query({
        query: CAT_ORGANIZATIONS_QUERY,
        fetchPolicy: "no-cache",
      })
      const orgs = response.data.adminCatOrganizations.map((org: any) => ({
        ...org,
        appIds: org.appIds || [],
      }))
      commit("setOrganizations", orgs)
    } catch (_) {
      commit("setOrganizations", [])
    }
    commit("setLoading", false)
  },

  async createOrganization(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    input: { name: string; status: string; appIds?: string[] }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: CREATE_CAT_ORGANIZATION_MUTATION,
        variables: { name: input.name },
      })
      const { status, message } = response.data.createCatOrganization
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message)
        dispatch("loadOrganizations")
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to create organization")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to create organization")
    }
    commit("setLoading", false)
  },

  async updateOrganization(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    input: { id: string; name?: string; status?: string }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: UPDATE_CAT_ORGANIZATION_MUTATION,
        variables: { id: input.id, name: input.name, status: input.status },
      })
      const { status, message } = response.data.updateCatOrganization
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message)
        dispatch("loadOrganizations")
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to update organization")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to update organization")
    }
    commit("setLoading", false)
  },

  async deleteOrganization({ commit, dispatch }: { commit: Commit; dispatch: any }, id: string) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: DELETE_CAT_ORGANIZATION_MUTATION,
        variables: { id },
      })
      const { status, message } = response.data.deleteCatOrganization
      if (status === "success") {
        commit("removeOrganization", id)
        emitAlert(EVENTS.GLOBAL_SUCCESS, message)
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to delete organization")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to delete organization")
    }
    commit("setLoading", false)
  },

  async loadOrganization({ commit }: { commit: Commit }, catOrganizationId: string) {
    commit("setLoading", true)
    try {
      const response = await client.query({
        query: GET_CAT_ORGANIZATION,
        variables: { catOrganizationId },
        fetchPolicy: "no-cache",
      })
      commit("setSelectedOrganization", response.data.adminCatOrganization)
    } catch (_) {
      commit("setSelectedOrganization", null)
    }
    commit("setLoading", false)
  },

  async loadOrgAppIds({ commit }: { commit: Commit }, catOrganizationId: string) {
    commit("setLoadingAppIds", true)
    try {
      const response = await client.query({
        query: GET_CAT_ORGANIZATION_APP_IDS,
        variables: { catOrganizationId },
        fetchPolicy: "no-cache",
      })
      commit("setOrgAppIds", response.data.adminCatOrganizationApplications)
    } catch (_) {
      commit("setOrgAppIds", [])
    }
    commit("setLoadingAppIds", false)
  },

  async loadOrgClientIds({ commit }: { commit: Commit }, catOrganizationId: string) {
    commit("setLoadingClientIds", true)
    try {
      const response = await client.query({
        query: GET_CAT_ORGANIZATION_CLIENT_IDS,
        variables: { catOrganizationId },
        fetchPolicy: "no-cache",
      })
      commit("setOrgClientIds", response.data.adminCatOrganizationClients)
    } catch (_) {
      commit("setOrgClientIds", [])
    }
    commit("setLoadingClientIds", false)
  },

  async loadOrgUsers({ commit }: { commit: Commit }, catOrganizationId: string) {
    commit("setLoadingUsers", true)
    try {
      const response = await client.query({
        query: GET_CAT_ORGANIZATION_USERS,
        variables: { catOrganizationId },
        fetchPolicy: "no-cache",
      })
      commit("setOrgUsers", response.data.adminUsers)
    } catch (_) {
      commit("setOrgUsers", [])
    }
    commit("setLoadingUsers", false)
  },

  async createCatOrgApplication(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    { catOrganizationId, appId }: { catOrganizationId: string; appId: string }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: CREATE_CAT_ORG_APP,
        variables: { catOrganizationId, appId },
      })
      const { status, message } = response.data.createCatOrganizationApplication
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message || "Application added")
        dispatch("loadOrgAppIds", catOrganizationId)
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to add application")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to add application")
    }
    commit("setLoading", false)
  },

  async updateCatOrgApplication(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    { id, catOrganizationId, appId, status: appStatus }: { id: string; catOrganizationId: string; appId?: string; status?: string }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: UPDATE_CAT_ORG_APP,
        variables: { id, appId, status: appStatus },
      })
      const { status, message } = response.data.updateCatOrganizationApplication
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message || "Application updated")
        dispatch("loadOrgAppIds", catOrganizationId)
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to update application")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to update application")
    }
    commit("setLoading", false)
  },

  async deleteCatOrgApplication(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    { id, catOrganizationId }: { id: string; catOrganizationId: string }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: DELETE_CAT_ORG_APP,
        variables: { id },
      })
      const { status, message } = response.data.deleteCatOrganizationApplication
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message || "Application removed")
        dispatch("loadOrgAppIds", catOrganizationId)
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to remove application")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to remove application")
    }
    commit("setLoading", false)
  },

  async createCatOrgClient(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    { catOrganizationId, clientId }: { catOrganizationId: string; clientId: string }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: CREATE_CAT_ORG_CLIENT,
        variables: { catOrganizationId, clientId },
      })
      const { status, message } = response.data.createCatOrganizationClient
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message || "Client added")
        dispatch("loadOrgClientIds", catOrganizationId)
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to add client")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to add client")
    }
    commit("setLoading", false)
  },

  async deleteCatOrgClient(
    { commit, dispatch }: { commit: Commit; dispatch: any },
    { id, catOrganizationId }: { id: string; catOrganizationId: string }
  ) {
    commit("setLoading", true)
    try {
      const response = await client.mutate({
        mutation: DELETE_CAT_ORG_CLIENT,
        variables: { id },
      })
      const { status, message } = response.data.deleteCatOrganizationClient
      if (status === "success") {
        emitAlert(EVENTS.GLOBAL_SUCCESS, message || "Client removed")
        dispatch("loadOrgClientIds", catOrganizationId)
      } else {
        emitAlert(EVENTS.GLOBAL_ERROR, message || "Failed to remove client")
      }
    } catch (_) {
      emitAlert(EVENTS.GLOBAL_ERROR, "Failed to remove client")
    }
    commit("setLoading", false)
  },
}
